import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "./PasswordInput";
import { FormError } from "./FormError";
import { toast } from "sonner";

/**
 * Reset Password Form Component
 *
 * Form to set a new password after clicking reset link.
 */
export function ResetPasswordForm() {
  const { updatePassword, loading, session } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("auth");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  // Check if user has a valid recovery session
  useEffect(() => {
    // Supabase automatically handles the recovery token from URL
    // and creates a session. If no session exists after a moment,
    // the link might be expired or invalid.
    const checkSession = setTimeout(() => {
      if (!session) {
        setError(t("resetPassword.linkExpired"));
      }
    }, 2000);

    return () => clearTimeout(checkSession);
  }, [session, t]);

  const validateForm = (): boolean => {
    const newErrors: {
      password?: string;
      confirmPassword?: string;
    } = {};

    if (!password) {
      newErrors.password = t("validation.passwordRequired");
    } else if (password.length < 8) {
      newErrors.password = t("validation.passwordMinLength");
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t("validation.confirmPasswordRequired");
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t("validation.passwordsDoNotMatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      await updatePassword(password);
      toast.success(t("resetPassword.successToast"));
      navigate({ to: "/login" });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update password. Please try again.";
      setError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={error} />

      <p className="text-sm text-muted-foreground">{t("resetPassword.subtitle")}</p>

      <PasswordInput
        label={t("resetPassword.passwordLabel")}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setErrors((prev) => ({ ...prev, password: undefined }));
        }}
        error={errors.password}
        disabled={loading}
        showStrengthIndicator
      />

      <PasswordInput
        id="confirm-new-password"
        label={t("resetPassword.confirmPasswordLabel")}
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }}
        error={errors.confirmPassword}
        disabled={loading}
      />

      <Button type="submit" className="h-11 w-full" disabled={loading}>
        {loading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            {t("resetPassword.submitting")}
          </>
        ) : (
          t("resetPassword.submit")
        )}
      </Button>
    </form>
  );
}

export default ResetPasswordForm;
